// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {confirmPrivyWallet} from '@app/features/auth/privy/PrivyApi';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import {useLoginWithEmail, usePrivy} from '@privy-io/react-auth';
import {useRef, useState} from 'react';

// Reopens Privy's wallet session while preserving the existing DAOI session/seat.
export function PrivyWalletRestoreLogin({address, onComplete}: {address: string; onComplete?: () => void}) {
	const {ready, getAccessToken, logout} = usePrivy();
	const {sendCode, loginWithCode} = useLoginWithEmail();
	const [email, setEmail] = useState('');
	const [code, setCode] = useState('');
	const [sent, setSent] = useState(false);
	const [busy, setBusy] = useState(false);
	const inFlight = useRef(false);
	const [error, setError] = useState<string | null>(null);
	const submit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (inFlight.current || !ready) return;
		inFlight.current = true;
		setBusy(true);
		setError(null);
		try {
			if (!sent) {
				await sendCode({email: email.trim(), disableSignup: true});
				setSent(true);
			} else {
				await loginWithCode({code: code.trim()});
				setCode('');
				const accessToken = await getAccessToken();
				if (!accessToken) throw new Error('Authentication required');
				// Server verifies the original DID and immutable business address. No /login exchange.
				const binding = await confirmPrivyWallet(RuntimeConfig.getSnapshot(), accessToken, address);
				if (binding.wallet_address?.toLowerCase() !== address.toLowerCase()) throw new Error('Wallet mismatch');
				onComplete?.();
			}
		} catch {
			setCode('');
			setSent(false);
			setError('无法恢复原钱包，请使用此账号原邮箱重新验证。');
			await logout().catch(() => {});
		} finally {
			inFlight.current = false;
			setBusy(false);
		}
	};
	return (
		<form onSubmit={(event) => void submit(event)} data-flx="auth.privy.wallet-restore-form">
			<Input
				label="原账号邮箱"
				type="email"
				value={email}
				autoComplete="email"
				required
				disabled={sent || busy}
				onChange={(event) => setEmail(event.target.value)}
				data-flx="auth.privy.wallet-restore-email"
			/>
			{sent && (
				<Input
					label="邮箱验证码"
					value={code}
					autoComplete="one-time-code"
					inputMode="numeric"
					required
					disabled={busy}
					onChange={(event) => setCode(event.target.value)}
					data-flx="auth.privy.wallet-restore-code"
				/>
			)}
			<Button
				fitContainer
				type="submit"
				disabled={!ready || busy}
				submitting={busy}
				data-flx="auth.privy.wallet-restore-submit"
			>
				{sent ? '验证并恢复原钱包' : '获取邮箱验证码'}
			</Button>
			{error && (
				<p role="alert" className="daoi-error" data-flx="auth.privy.wallet-restore-error">
					{error}
				</p>
			)}
		</form>
	);
}
